import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useTranslation } from "../context/I18nContext";
import StudentShell from "../components/StudentShell/StudentShell";
import Pagination from "../components/Pagination/Pagination";
import * as classesService from "../services/classes";
import * as lessonsService from "../services/lessons";
import * as studentsService from "../services/students";
import { localizeField } from "../utils/localize";
import useTimelineStartScroll from "../utils/useTimelineStartScroll";
import {
  dedupeTeacherSessions,
  getTeacherSessionKey,
} from "../utils/teacherSessionDedupe";
import "./Lessons.css";
import "../styles/admin-timetable.css";

const STATUS_LABELS_KEY = {
  scheduled: "lessons.statusScheduled",
  completed: "lessons.statusCompleted",
  cancelled: "lessons.statusCancelled",
};

const STATUS_FILTERS = [
  { value: "all", labelKey: "lessons.filterAll" },
  { value: "scheduled", labelKey: "lessons.filterScheduled" },
  { value: "completed", labelKey: "lessons.filterCompleted" },
  { value: "cancelled", labelKey: "lessons.filterCancelled" },
];

function formatDateTime(value, lang = "ar") {
  if (!value) return "-";
  try {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    const locale = lang === "ar" ? "ar-TN" : lang;
    return new Intl.DateTimeFormat(locale, {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(date);
  } catch (error) {
    return value;
  }
}

function lessonMatchesSearch(lesson, query, lang) {
  if (!query) return true;
  const haystack = [
    localizeField(lesson, "title", lang),
    localizeField(lesson, "description", lang),
    localizeField(lesson.class_group, "name", lang),
    localizeField(lesson.student, "full_name", lang),
    localizeField(lesson.teacher, "full_name", lang),
    lesson.teacher?.email,
    localizeField(lesson.pack, "name", lang),
  ]
    .filter(Boolean)
    .map((entry) => entry.toString().toLowerCase());
  return haystack.some((entry) => entry.includes(query));
}

function statusClassName(status) {
  if (!status) return "status";
  return `status status--${status}`;
}

function clampNumber(value, min, max) {
  const numeric = Number(value);
  if (Number.isNaN(numeric)) return min;
  return Math.min(max, Math.max(min, numeric));
}

function toDateInputValue(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function parseDateInput(value) {
  if (!value) return null;
  const parts = String(value)
    .split("-")
    .map((p) => Number(p));
  if (parts.length !== 3) return null;
  const [year, month, day] = parts;
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
}

function normalizeToLocalDayStart(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return null;
  return new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    0,
    0,
    0,
    0,
  );
}

function ChildTimetable({ child, lessons, selectedDate, locale, language, t }) {
  const teachersBodyRef = useRef(null);
  const canvasRef = useRef(null);
  const syncingScrollRef = useRef(false);

  const startHour = 0;
  const endHour = 24;
  const hoursCount = endHour - startHour;
  const visibleStartMinutes = startHour * 60;
  const visibleEndMinutes = endHour * 60;
  const visibleMinutes = visibleEndMinutes - visibleStartMinutes;

  const [hourWidth, setHourWidth] = useState(110);
  const timelineWidth = hoursCount * hourWidth;
  const pxPerMinute = timelineWidth / visibleMinutes;

  useLayoutEffect(() => {
    const el = canvasRef.current;
    if (!el) return;

    const update = () => {
      const w = el.clientWidth;
      if (!w) return;
      const nextHourWidth = clampNumber(Math.ceil(w / hoursCount), 60, 200);
      setHourWidth(nextHourWidth);
    };

    update();
    const ro = new ResizeObserver(() => update());
    ro.observe(el);
    return () => ro.disconnect();
  }, [hoursCount]);

  const weekWindow = useMemo(() => {
    const base = new Date(
      selectedDate.getFullYear(),
      selectedDate.getMonth(),
      selectedDate.getDate(),
      0,
      0,
      0,
      0,
    );
    // Monday as the first day of week.
    const dow = base.getDay(); // 0..6 (Sun..Sat)
    const diffToMonday = (dow + 6) % 7;
    const start = new Date(base);
    start.setDate(start.getDate() - diffToMonday);
    const end = new Date(start);
    end.setDate(end.getDate() + 7);
    return { start, end };
  }, [selectedDate]);

  const lessonsForChild = useMemo(() => {
    const childId = child?.id;
    if (!childId) return [];
    return (lessons || []).filter((lesson) => lesson?.student?.id === childId);
  }, [lessons, child]);

  const weekDays = useMemo(() => {
    const fmt = new Intl.DateTimeFormat(locale, {
      weekday: "long",
      month: "short",
      day: "numeric",
    });

    return Array.from({ length: 7 }, (_, i) => {
      const date = new Date(weekWindow.start);
      date.setDate(date.getDate() + i);
      return {
        key: toDateInputValue(date),
        date,
        label: fmt.format(date),
      };
    });
  }, [weekWindow, locale]);

  const lessonsByDay = useMemo(() => {
    const map = new Map();
    weekDays.forEach((d) => map.set(d.key, []));

    const inWeek = (when) => when >= weekWindow.start && when < weekWindow.end;

    lessonsForChild.forEach((lesson) => {
      const when = new Date(lesson?.scheduled_at);
      if (Number.isNaN(when.getTime())) return;

      if (!lesson?.is_recurring_weekly) {
        if (!inWeek(when)) return;
        const key = toDateInputValue(when);
        if (map.has(key)) map.get(key).push(lesson);
        return;
      }

      const startDay = normalizeToLocalDayStart(when);
      if (!startDay) return;

      weekDays.forEach((d) => {
        if (d.date.getDay() !== when.getDay()) return;
        const dayStart = normalizeToLocalDayStart(d.date);
        if (!dayStart) return;
        if (dayStart < startDay) return;
        map.get(d.key).push(lesson);
      });
    });

    map.forEach((arr) => {
      arr.sort((a, b) => {
        const da = new Date(a?.scheduled_at);
        const db = new Date(b?.scheduled_at);
        return da - db;
      });
    });

    return map;
  }, [lessonsForChild, weekDays, weekWindow]);

  const totalLessonsInWeek = useMemo(() => {
    let total = 0;
    lessonsByDay.forEach((arr) => {
      total += arr.length;
    });
    return total;
  }, [lessonsByDay]);

  useTimelineStartScroll(canvasRef, hourWidth, [selectedDate, lessons]);

  const timelineHours = useMemo(() => {
    return Array.from({ length: hoursCount }, (_, i) => {
      const hour = startHour + i;
      return {
        hour,
        label: `${String(hour).padStart(2, "0")}:00`,
        left: (i + 0.5) * hourWidth,
      };
    });
  }, [hourWidth, hoursCount, startHour]);

  const syncScroll = (source) => {
    if (syncingScrollRef.current) return;
    syncingScrollRef.current = true;
    try {
      if (source === "canvas") {
        if (teachersBodyRef.current && canvasRef.current) {
          teachersBodyRef.current.scrollTop = canvasRef.current.scrollTop;
        }
      } else {
        if (teachersBodyRef.current && canvasRef.current) {
          canvasRef.current.scrollTop = teachersBodyRef.current.scrollTop;
        }
      }
    } finally {
      syncingScrollRef.current = false;
    }
  };

  return (
    <div className="admin-timetable admin-timetable--full">
      <div className="admin-timetable__toolbar">
        <div className="admin-timetable__meta text-muted">
          {new Intl.DateTimeFormat(locale, { dateStyle: "full" }).format(
            selectedDate,
          )}
        </div>
      </div>

      <div className="admin-timetable__grid">
        <aside className="admin-timetable__teachers">
          <div className="admin-timetable__teachersHead">
            <div className="admin-timetable__teachersTitle">
              {t("lessons.timetable.days")}
            </div>
          </div>
          <div
            className="admin-timetable__teachersBody"
            ref={teachersBodyRef}
            onScroll={() => syncScroll("teachers")}
          >
            {weekDays.map((d) => {
              const count = (lessonsByDay.get(d.key) || []).length;
              return (
                <div className="admin-timetable__teacherRow" key={d.key}>
                  <div className="admin-timetable__teacherName">{d.label}</div>
                  <div className="admin-timetable__teacherCount">
                    {count}{" "}
                    {count === 1
                      ? t("lessons.timetable.item")
                      : t("lessons.timetable.items")}
                  </div>
                </div>
              );
            })}
            {!totalLessonsInWeek && (
              <div className="admin-timetable__empty">
                {t("lessons.timetable.noLessonsForWeek")}
              </div>
            )}
          </div>
        </aside>

        <div
          className="admin-timetable__canvas"
          ref={canvasRef}
          onScroll={() => syncScroll("canvas")}
          style={{
            "--timetable-width": `${timelineWidth}px`,
            "--timetable-hour": `${hourWidth}px`,
          }}
        >
          <div className="admin-timetable__times">
            <div className="admin-timetable__timesInner">
              {timelineHours.map((h) => (
                <div
                  key={h.hour}
                  className="admin-timetable__timeTick"
                  style={{ insetInlineStart: `${h.left}px` }}
                >
                  <div className="admin-timetable__timeHour">{h.label}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="admin-timetable__rows">
            {weekDays.map((day, rowIndex) => {
              const dayLessons = lessonsByDay.get(day.key) || [];
              return (
                <div key={day.key} className="admin-timetable__row">
                  {dayLessons.map((lesson) => {
                    const when = new Date(lesson?.scheduled_at);
                    if (Number.isNaN(when.getTime())) return null;
                    const startMinutes =
                      when.getHours() * 60 + when.getMinutes();
                    const duration = Number(lesson.duration_minutes) || 60;

                    const endMinutes = startMinutes + duration;
                    if (
                      endMinutes <= visibleStartMinutes ||
                      startMinutes >= visibleEndMinutes
                    ) {
                      return null;
                    }

                    const clippedStart = Math.max(
                      startMinutes,
                      visibleStartMinutes,
                    );
                    const clippedEnd = Math.min(endMinutes, visibleEndMinutes);
                    const left =
                      (clippedStart - visibleStartMinutes) * pxPerMinute;
                    const width = clampNumber(
                      (clippedEnd - clippedStart) * pxPerMinute,
                      18,
                      timelineWidth,
                    );
                    const style = {
                      insetInlineStart: `${left}px`,
                      width: `${width}px`,
                    };
                    const colorClass =
                      rowIndex % 3 === 0
                        ? "admin-timetable__event--a"
                        : rowIndex % 3 === 1
                          ? "admin-timetable__event--b"
                          : "admin-timetable__event--c";

                    const teacherName =
                      localizeField(lesson.teacher, "full_name", language) ||
                      lesson?.teacher?.full_name ||
                      lesson?.teacher?.email ||
                      "";
                    const subtitle = teacherName || lesson.status || "";

                    return (
                      <div
                        key={lesson.id}
                        className={`admin-timetable__event ${colorClass}`}
                        style={style}
                        title={lesson.title}
                      >
                        <div className="admin-timetable__eventTitle">
                          {lesson.title}
                        </div>
                        <div className="admin-timetable__eventSub">
                          {subtitle}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

function ParentLessonsTimetable({
  t,
  language,
  locale,
  students,
  lessons,
  classGroups,
}) {
  const [selectedDate, setSelectedDate] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate());
  });

  const [selectedChildId, setSelectedChildId] = useState(() => {
    const first = students?.[0]?.id;
    return first ? String(first) : "";
  });

  useEffect(() => {
    const first = students?.[0]?.id;
    const firstId = first ? String(first) : "";
    setSelectedChildId((current) => {
      if (!students?.length) return "";
      if (current && students.some((s) => String(s.id) === String(current))) {
        return String(current);
      }
      return firstId;
    });
  }, [students]);

  const shiftWeek = (deltaWeeks) => {
    setSelectedDate((current) => {
      const next = new Date(current);
      next.setDate(next.getDate() + deltaWeeks * 7);
      return next;
    });
  };

  const goToToday = () => {
    const now = new Date();
    setSelectedDate(new Date(now.getFullYear(), now.getMonth(), now.getDate()));
  };

  const weekRangeLabel = useMemo(() => {
    const base = new Date(
      selectedDate.getFullYear(),
      selectedDate.getMonth(),
      selectedDate.getDate(),
      0,
      0,
      0,
      0,
    );
    const dow = base.getDay();
    const diffToMonday = (dow + 6) % 7;
    const start = new Date(base);
    start.setDate(start.getDate() - diffToMonday);
    const end = new Date(start);
    end.setDate(end.getDate() + 6);

    const fmt = new Intl.DateTimeFormat(locale, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
    return `${fmt.format(start)} – ${fmt.format(end)}`;
  }, [selectedDate, locale]);

  const selectedChild = useMemo(() => {
    if (!students?.length) return null;
    return (
      students.find((s) => String(s.id) === String(selectedChildId)) ||
      students[0]
    );
  }, [students, selectedChildId]);

  const selectedChildClassLabel = useMemo(() => {
    if (!selectedChild?.id) return null;
    const groups = (classGroups || []).filter((g) =>
      (g.student_ids || []).includes(selectedChild.id),
    );
    if (!groups.length) return null;
    try {
      const formatter = new Intl.ListFormat(locale, {
        style: "long",
        type: "conjunction",
      });
      return formatter.format(groups.map((g) => g.name));
    } catch (e) {
      return groups.map((g) => g.name).join(language === "ar" ? "، " : ", ");
    }
  }, [classGroups, selectedChild?.id, language, locale]);

  return (
    <div className="lessons-parent-timetable">
      <div className="admin-timetable-topcard">
        <div className="admin-timetable-topcard__head">
          <div className="admin-timetable-topcard__title">
            <div className="admin-timetable-topcard__icon" aria-hidden="true">
              <i className="fa-solid fa-calendar-days"></i>
            </div>
            <div>
              <div className="admin-timetable-topcard__titleText">
                {t("adminPage.tabs.timetable")}
              </div>
              <div className="admin-timetable-topcard__subtitle">
                {weekRangeLabel}
              </div>
            </div>
          </div>
        </div>

        <div className="admin-timetable-topcard__controls">
          <div className="admin-timetable-topcard__dateControls">
            <div className="admin-timetable-topcard__datePill lessons-parent-timetable__childPill">
              <select
                className="admin-timetable-topcard__dateInput lessons-parent-timetable__childSelect"
                value={selectedChildId}
                onChange={(e) => setSelectedChildId(e.target.value)}
                aria-label={t("lessons.timetable.selectChild")}
                disabled={!students?.length}
              >
                {(students || []).map((child) => (
                  <option key={child.id} value={String(child.id)}>
                    {child.full_name}
                  </option>
                ))}
              </select>
            </div>

            <button
              type="button"
              className="admin-timetable-topcard__pillBtn"
              onClick={() => shiftWeek(-1)}
              aria-label={t("lessons.timetable.previousWeek")}
            >
              <i className="fa-solid fa-chevron-left" aria-hidden="true"></i>
            </button>
            <div className="admin-timetable-topcard__datePill">
              <input
                className="admin-timetable-topcard__dateInput"
                type="date"
                value={toDateInputValue(selectedDate)}
                onChange={(e) => {
                  const parsed = parseDateInput(e.target.value);
                  if (parsed) setSelectedDate(parsed);
                }}
              />
            </div>
            <button
              type="button"
              className="admin-timetable-topcard__pillBtn"
              onClick={() => shiftWeek(1)}
              aria-label={t("lessons.timetable.nextWeek")}
            >
              <i className="fa-solid fa-chevron-right" aria-hidden="true"></i>
            </button>

            <button
              type="button"
              className="admin-timetable-topcard__pillBtn"
              onClick={goToToday}
            >
              {t("lessons.timetable.today")}
            </button>
          </div>
        </div>
      </div>

      {!students?.length ? (
        <div className="lessons-empty">
          <p>{t("lessons.empty")}</p>
        </div>
      ) : selectedChild ? (
        <section className="lessons-parent-timetable__child">
          <h3 className="lessons-parent-timetable__childTitle">
            {selectedChild.full_name}
          </h3>
          {selectedChildClassLabel && (
            <p className="lessons-muted" style={{ marginTop: 0 }}>
              {t("lessons.timetable.classLabel")} {selectedChildClassLabel}
            </p>
          )}
          <ChildTimetable
            child={selectedChild}
            lessons={lessons}
            selectedDate={selectedDate}
            locale={locale}
            language={language}
            t={t}
          />
        </section>
      ) : null}
    </div>
  );
}

function TeacherTimetable({
  teacher,
  lessons,
  selectedDate,
  locale,
  language,
  t,
}) {
  const peopleBodyRef = useRef(null);
  const canvasRef = useRef(null);
  const syncingScrollRef = useRef(false);

  const startHour = 0;
  const endHour = 24;
  const hoursCount = endHour - startHour;
  const visibleStartMinutes = startHour * 60;
  const visibleEndMinutes = endHour * 60;
  const visibleMinutes = visibleEndMinutes - visibleStartMinutes;

  const [hourWidth, setHourWidth] = useState(110);
  const timelineWidth = hoursCount * hourWidth;
  const pxPerMinute = timelineWidth / visibleMinutes;

  useLayoutEffect(() => {
    const el = canvasRef.current;
    if (!el) return;

    const update = () => {
      const w = el.clientWidth;
      if (!w) return;
      const nextHourWidth = clampNumber(Math.ceil(w / hoursCount), 60, 200);
      setHourWidth(nextHourWidth);
    };

    update();
    const ro = new ResizeObserver(() => update());
    ro.observe(el);
    return () => ro.disconnect();
  }, [hoursCount]);

  const weekWindow = useMemo(() => {
    const base = new Date(
      selectedDate.getFullYear(),
      selectedDate.getMonth(),
      selectedDate.getDate(),
      0,
      0,
      0,
      0,
    );
    // Monday as the first day of week.
    const dow = base.getDay(); // 0..6 (Sun..Sat)
    const diffToMonday = (dow + 6) % 7;
    const start = new Date(base);
    start.setDate(start.getDate() - diffToMonday);
    const end = new Date(start);
    end.setDate(end.getDate() + 7);
    return { start, end };
  }, [selectedDate]);

  const lessonsForTeacher = useMemo(() => {
    const teacherId = teacher?.id;
    const teacherEmail = teacher?.email;
    if (!teacherId && !teacherEmail) return [];

    return (lessons || []).filter((lesson) => {
      const t = lesson?.teacher;
      if (!t) return false;
      if (teacherId && t.id) return String(t.id) === String(teacherId);
      if (teacherEmail && t.email) {
        return (
          String(t.email).toLowerCase() === String(teacherEmail).toLowerCase()
        );
      }
      return false;
    });
  }, [lessons, teacher]);

  const sessionLessonsForTeacher = useMemo(
    () => dedupeTeacherSessions(lessonsForTeacher),
    [lessonsForTeacher],
  );

  const weekDays = useMemo(() => {
    const fmt = new Intl.DateTimeFormat(locale, {
      weekday: "long",
      month: "short",
      day: "numeric",
    });

    return Array.from({ length: 7 }, (_, i) => {
      const date = new Date(weekWindow.start);
      date.setDate(date.getDate() + i);
      return {
        key: toDateInputValue(date),
        date,
        label: fmt.format(date),
      };
    });
  }, [weekWindow, locale]);

  const lessonsByDay = useMemo(() => {
    const map = new Map();
    weekDays.forEach((d) => map.set(d.key, []));

    const inWeek = (when) => when >= weekWindow.start && when < weekWindow.end;

    sessionLessonsForTeacher.forEach((lesson) => {
      const when = new Date(lesson?.scheduled_at);
      if (Number.isNaN(when.getTime())) return;

      if (!lesson?.is_recurring_weekly) {
        if (!inWeek(when)) return;
        const key = toDateInputValue(when);
        if (map.has(key)) map.get(key).push(lesson);
        return;
      }

      const startDay = normalizeToLocalDayStart(when);
      if (!startDay) return;

      weekDays.forEach((d) => {
        if (d.date.getDay() !== when.getDay()) return;
        const dayStart = normalizeToLocalDayStart(d.date);
        if (!dayStart) return;
        if (dayStart < startDay) return;
        map.get(d.key).push(lesson);
      });
    });

    map.forEach((arr) => {
      arr.sort((a, b) => {
        const da = new Date(a?.scheduled_at);
        const db = new Date(b?.scheduled_at);
        return da - db;
      });
    });

    return map;
  }, [sessionLessonsForTeacher, weekDays, weekWindow]);

  const totalLessonsInWeek = useMemo(() => {
    let total = 0;
    lessonsByDay.forEach((arr) => {
      total += arr.length;
    });
    return total;
  }, [lessonsByDay]);

  useTimelineStartScroll(canvasRef, hourWidth, [selectedDate, lessons]);

  const timelineHours = useMemo(() => {
    return Array.from({ length: hoursCount }, (_, i) => {
      const hour = startHour + i;
      return {
        hour,
        label: `${String(hour).padStart(2, "0")}:00`,
        left: (i + 0.5) * hourWidth,
      };
    });
  }, [hourWidth, hoursCount, startHour]);

  const syncScroll = (source) => {
    if (syncingScrollRef.current) return;
    syncingScrollRef.current = true;
    try {
      if (source === "canvas") {
        if (peopleBodyRef.current && canvasRef.current) {
          peopleBodyRef.current.scrollTop = canvasRef.current.scrollTop;
        }
      } else {
        if (peopleBodyRef.current && canvasRef.current) {
          canvasRef.current.scrollTop = peopleBodyRef.current.scrollTop;
        }
      }
    } finally {
      syncingScrollRef.current = false;
    }
  };

  return (
    <div className="admin-timetable admin-timetable--full">
      <div className="admin-timetable__toolbar">
        <div className="admin-timetable__meta text-muted">
          {new Intl.DateTimeFormat(locale, { dateStyle: "full" }).format(
            selectedDate,
          )}
        </div>
      </div>

      <div className="admin-timetable__grid">
        <aside className="admin-timetable__teachers">
          <div className="admin-timetable__teachersHead">
            <div className="admin-timetable__teachersTitle">
              {t("lessons.timetable.days")}
            </div>
          </div>
          <div
            className="admin-timetable__teachersBody"
            ref={peopleBodyRef}
            onScroll={() => syncScroll("people")}
          >
            {weekDays.map((d) => {
              const count = (lessonsByDay.get(d.key) || []).length;
              return (
                <div className="admin-timetable__teacherRow" key={d.key}>
                  <div className="admin-timetable__teacherName">{d.label}</div>
                  <div className="admin-timetable__teacherCount">
                    {count}{" "}
                    {count === 1
                      ? t("lessons.timetable.item")
                      : t("lessons.timetable.items")}
                  </div>
                </div>
              );
            })}
            {!totalLessonsInWeek && (
              <div className="admin-timetable__empty">
                {t("lessons.timetable.noLessonsForWeek")}
              </div>
            )}
          </div>
        </aside>

        <div
          className="admin-timetable__canvas"
          ref={canvasRef}
          onScroll={() => syncScroll("canvas")}
          style={{
            "--timetable-width": `${timelineWidth}px`,
            "--timetable-hour": `${hourWidth}px`,
          }}
        >
          <div className="admin-timetable__times">
            <div className="admin-timetable__timesInner">
              {timelineHours.map((h) => (
                <div
                  key={h.hour}
                  className="admin-timetable__timeTick"
                  style={{ insetInlineStart: `${h.left}px` }}
                >
                  <div className="admin-timetable__timeHour">{h.label}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="admin-timetable__rows">
            {weekDays.map((day, rowIndex) => {
              const rowLessons = lessonsByDay.get(day.key) || [];

              return (
                <div key={day.key} className="admin-timetable__row">
                  {rowLessons.map((lesson) => {
                    const when = new Date(lesson?.scheduled_at);
                    if (Number.isNaN(when.getTime())) return null;
                    const startMinutes =
                      when.getHours() * 60 + when.getMinutes();
                    const duration = Number(lesson.duration_minutes) || 60;

                    const endMinutes = startMinutes + duration;
                    if (
                      endMinutes <= visibleStartMinutes ||
                      startMinutes >= visibleEndMinutes
                    ) {
                      return null;
                    }

                    const clippedStart = Math.max(
                      startMinutes,
                      visibleStartMinutes,
                    );
                    const clippedEnd = Math.min(endMinutes, visibleEndMinutes);
                    const left =
                      (clippedStart - visibleStartMinutes) * pxPerMinute;
                    const width = clampNumber(
                      (clippedEnd - clippedStart) * pxPerMinute,
                      18,
                      timelineWidth,
                    );
                    const style = {
                      insetInlineStart: `${left}px`,
                      width: `${width}px`,
                    };
                    const colorClass =
                      rowIndex % 3 === 0
                        ? "admin-timetable__event--a"
                        : rowIndex % 3 === 1
                          ? "admin-timetable__event--b"
                          : "admin-timetable__event--c";

                    const studentName =
                      localizeField(lesson.student, "full_name", language) ||
                      lesson?.student?.full_name ||
                      "";
                    const persistedClassName =
                      localizeField(lesson.class_group, "name", language) ||
                      lesson?.class_group?.name ||
                      "";
                    const subtitle = persistedClassName || studentName;

                    return (
                      <Link
                        key={getTeacherSessionKey(lesson) || lesson.id}
                        to={`/lessons/${lesson.id}/edit`}
                        className={`admin-timetable__event ${colorClass}`}
                        style={style}
                        title={
                          localizeField(lesson, "title", language) ||
                          lesson.title
                        }
                      >
                        <div className="admin-timetable__eventTitle">
                          {localizeField(lesson, "title", language) ||
                            lesson.title}
                        </div>
                        <div className="admin-timetable__eventSub">
                          {subtitle}
                        </div>
                      </Link>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

function TeacherLessonsTimetable({ t, language, locale, teacher, lessons }) {
  const [selectedDate, setSelectedDate] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate());
  });

  const shiftWeek = (deltaWeeks) => {
    setSelectedDate((current) => {
      const next = new Date(current);
      next.setDate(next.getDate() + deltaWeeks * 7);
      return next;
    });
  };

  const goToToday = () => {
    const now = new Date();
    setSelectedDate(new Date(now.getFullYear(), now.getMonth(), now.getDate()));
  };

  const weekRangeLabel = useMemo(() => {
    const base = new Date(
      selectedDate.getFullYear(),
      selectedDate.getMonth(),
      selectedDate.getDate(),
      0,
      0,
      0,
      0,
    );
    const dow = base.getDay();
    const diffToMonday = (dow + 6) % 7;
    const start = new Date(base);
    start.setDate(start.getDate() - diffToMonday);
    const end = new Date(start);
    end.setDate(end.getDate() + 6);

    const fmt = new Intl.DateTimeFormat(locale, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
    return `${fmt.format(start)} – ${fmt.format(end)}`;
  }, [selectedDate, locale]);

  return (
    <div className="lessons-parent-timetable">
      <div className="admin-timetable-topcard">
        <div className="admin-timetable-topcard__head">
          <div className="admin-timetable-topcard__title">
            <div className="admin-timetable-topcard__icon" aria-hidden="true">
              <i className="fa-solid fa-calendar-days"></i>
            </div>
            <div>
              <div className="admin-timetable-topcard__titleText">
                {t("adminPage.tabs.timetable")}
              </div>
              <div className="admin-timetable-topcard__subtitle">
                {weekRangeLabel}
              </div>
            </div>
          </div>
        </div>

        <div className="admin-timetable-topcard__controls">
          <div className="admin-timetable-topcard__dateControls">
            <button
              type="button"
              className="admin-timetable-topcard__pillBtn"
              onClick={() => shiftWeek(-1)}
              aria-label={t("lessons.timetable.previousWeek")}
            >
              <i className="fa-solid fa-chevron-left" aria-hidden="true"></i>
            </button>
            <div className="admin-timetable-topcard__datePill">
              <input
                className="admin-timetable-topcard__dateInput"
                type="date"
                value={toDateInputValue(selectedDate)}
                onChange={(e) => {
                  const parsed = parseDateInput(e.target.value);
                  if (parsed) setSelectedDate(parsed);
                }}
              />
            </div>
            <button
              type="button"
              className="admin-timetable-topcard__pillBtn"
              onClick={() => shiftWeek(1)}
              aria-label={t("lessons.timetable.nextWeek")}
            >
              <i className="fa-solid fa-chevron-right" aria-hidden="true"></i>
            </button>

            <button
              type="button"
              className="admin-timetable-topcard__pillBtn"
              onClick={goToToday}
            >
              {t("lessons.timetable.today")}
            </button>
          </div>

          <div className="admin-timetable-topcard__rightControls">
            <div className="admin-timetable-topcard__seg">
              <button
                type="button"
                className="admin-timetable-topcard__segBtn"
                disabled
              >
                {t("lessons.timetable.month")}
              </button>
              <button
                type="button"
                className="admin-timetable-topcard__segBtn admin-timetable-topcard__segBtn--active"
              >
                {t("lessons.timetable.week")}
              </button>
            </div>
          </div>
        </div>
      </div>

      <TeacherTimetable
        teacher={teacher}
        lessons={lessons}
        selectedDate={selectedDate}
        locale={locale}
        language={language}
        t={t}
      />
    </div>
  );
}

export default function Lessons() {
  const {
    effectiveUser: user,
    accessToken,
    activeStudentId,
    effectiveRole,
  } = useAuth();
  const { t, language } = useTranslation();
  const location = useLocation();
  const [lessons, setLessons] = useState([]);
  const [students, setStudents] = useState([]);
  const [classGroups, setClassGroups] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [lessonsPage, setLessonsPage] = useState(1);
  const [lessonsTotalPages, setLessonsTotalPages] = useState(1);

  const canManage = Boolean(user && ["admin", "teacher"].includes(user.role));

  const loadLessons = useCallback(
    async (page = 1) => {
      if (!accessToken) return;

      const isListView = !["parent", "teacher"].includes(effectiveRole);

      setLoading(true);
      setError(null);
      try {
        const payload = await lessonsService.fetchLessons({
          accessToken,
          params:
            isListView && effectiveRole === "admin"
              ? { page, session: 1 }
              : isListView
                ? { page }
                : undefined,
        });
        const allLessons = payload.lessons || [];
        if (effectiveRole === "student" && activeStudentId) {
          setLessons(
            allLessons.filter(
              (lesson) => lesson.student?.id === activeStudentId,
            ),
          );
        } else {
          setLessons(allLessons);
        }

        if (isListView) {
          setLessonsPage(payload.page || page || 1);
          setLessonsTotalPages(payload.total_pages || 1);
        } else {
          setLessonsPage(1);
          setLessonsTotalPages(1);
        }

        if (effectiveRole === "parent") {
          try {
            const studentsPayload = await studentsService.fetchStudents({
              accessToken,
            });
            setStudents(studentsPayload.students || []);
          } catch (studentError) {
            if (studentError.status !== 403) {
              setError(studentError.message);
            }
            setStudents([]);
          }
        } else {
          setStudents([]);
        }
      } catch (loadError) {
        setError(loadError.message);
      } finally {
        setLoading(false);
      }
    },
    [accessToken, effectiveRole, activeStudentId],
  );

  useEffect(() => {
    loadLessons(lessonsPage);
  }, [loadLessons, refreshKey, lessonsPage]);

  useEffect(() => {
    if (!accessToken || !["teacher", "parent"].includes(effectiveRole)) {
      setClassGroups([]);
      return;
    }

    let ignore = false;
    async function loadClasses() {
      try {
        const payload = await classesService.fetchClasses({ accessToken });
        if (!ignore) {
          setClassGroups(payload.classes || []);
        }
      } catch (e) {
        if (!ignore) {
          setClassGroups([]);
        }
      }
    }

    loadClasses();
    return () => {
      ignore = true;
    };
  }, [accessToken, effectiveRole]);

  const filteredLessons = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    const source =
      effectiveRole === "admin" ? dedupeTeacherSessions(lessons) : lessons;

    return source.filter((lesson) => {
      if (statusFilter !== "all" && lesson.status !== statusFilter) {
        return false;
      }
      return lessonMatchesSearch(lesson, query, language);
    });
  }, [lessons, effectiveRole, statusFilter, searchTerm, language]);

  const handleDelete = async (lessonId) => {
    if (!canManage) return;
    const confirmed = window.confirm(t("lessons.deleteConfirm"));
    if (!confirmed) return;

    try {
      await lessonsService.deleteLesson(accessToken, lessonId);
      setLessons((current) =>
        current.filter((lesson) => lesson.id !== lessonId),
      );
    } catch (deleteError) {
      setError(deleteError.message);
    }
  };

  const handleRefresh = () => setRefreshKey((value) => value + 1);

  const locale = useMemo(() => {
    return language === "ar" ? "ar-EG-u-nu-latn" : language || "en";
  }, [language]);

  const showStudentTimetable = useMemo(() => {
    if (effectiveRole !== "student") return false;
    const params = new URLSearchParams(location.search);
    return params.get("view") === "timetable";
  }, [effectiveRole, location.search]);

  const [selectedDate, setSelectedDate] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate());
  });

  const shiftWeek = (deltaWeeks) => {
    setSelectedDate((current) => {
      const next = new Date(current);
      next.setDate(next.getDate() + deltaWeeks * 7);
      return next;
    });
  };

  const goToToday = () => {
    const now = new Date();
    setSelectedDate(new Date(now.getFullYear(), now.getMonth(), now.getDate()));
  };

  const activeChild = useMemo(() => {
    if (effectiveRole !== "student") return null;
    const fromLesson = lessons.find((l) => l?.student?.id)?.student;
    if (fromLesson) return fromLesson;
    if (activeStudentId) {
      return {
        id: activeStudentId,
        full_name: user?.full_name || user?.email || "",
      };
    }
    return null;
  }, [effectiveRole, activeStudentId, user, lessons]);

  const showTimetableOnly =
    effectiveRole === "parent" || effectiveRole === "teacher";

  const page = (
    <div className="lessons-page">
      <div className="lessons-shell">
        <header className="lessons-header">
          <div>
            <h1>{t("lessons.title")}</h1>
            <p>
              {effectiveRole === "student"
                ? t("lessons.subtitleStudent")
                : effectiveRole === "parent"
                  ? t("lessons.subtitleStudent")
                  : t("lessons.subtitleStaff")}
            </p>
          </div>
          {canManage && !showTimetableOnly && (
            <Link to="/lessons/new" className="btn primary">
              {t("lessons.newLesson")}
            </Link>
          )}
        </header>

        {effectiveRole === "parent" ? (
          loading ? (
            <p className="lessons-muted">{t("lessons.loading")}</p>
          ) : (
            <ParentLessonsTimetable
              t={t}
              language={language}
              locale={locale}
              students={students}
              lessons={lessons}
              classGroups={classGroups}
            />
          )
        ) : effectiveRole === "teacher" ? (
          loading ? (
            <p className="lessons-muted">{t("lessons.loading")}</p>
          ) : (
            <TeacherLessonsTimetable
              t={t}
              language={language}
              locale={locale}
              teacher={user}
              lessons={lessons}
              classGroups={classGroups}
            />
          )
        ) : showStudentTimetable ? (
          loading ? (
            <p className="lessons-muted">{t("lessons.loading")}</p>
          ) : activeChild ? (
            <div className="lessons-parent-timetable">
              <div className="admin-timetable-topcard">
                <div className="admin-timetable-topcard__head">
                  <div className="admin-timetable-topcard__title">
                    <div
                      className="admin-timetable-topcard__icon"
                      aria-hidden="true"
                    >
                      <i className="fa-solid fa-calendar-days"></i>
                    </div>
                    <div>
                      <div className="admin-timetable-topcard__titleText">
                        {t("lessons.timetable.yourWeeklyTimetable")}
                      </div>
                      <div className="admin-timetable-topcard__subtitle">
                        {activeChild.full_name}
                      </div>
                    </div>
                  </div>
                  <div className="admin-timetable-topcard__controls">
                    <button
                      type="button"
                      className="admin-timetable-topcard__pillBtn"
                      onClick={() => shiftWeek(-1)}
                      aria-label={t("lessons.timetable.previousWeek")}
                    >
                      <i
                        className="fa-solid fa-chevron-left"
                        aria-hidden="true"
                      ></i>
                    </button>
                    <div className="admin-timetable-topcard__datePill">
                      <input
                        type="date"
                        className="admin-timetable-topcard__dateInput"
                        value={toDateInputValue(selectedDate)}
                        onChange={(e) => {
                          const parsed = parseDateInput(e.target.value);
                          if (parsed) setSelectedDate(parsed);
                        }}
                      />
                    </div>
                    <button
                      type="button"
                      className="admin-timetable-topcard__pillBtn"
                      onClick={() => shiftWeek(1)}
                      aria-label={t("lessons.timetable.nextWeek")}
                    >
                      <i
                        className="fa-solid fa-chevron-right"
                        aria-hidden="true"
                      ></i>
                    </button>
                    <button
                      type="button"
                      className="admin-timetable-topcard__pillBtn"
                      onClick={goToToday}
                    >
                      {t("lessons.timetable.today")}
                    </button>
                  </div>
                </div>
              </div>

              <section className="lessons-parent-timetable__child">
                <h3 className="lessons-parent-timetable__childTitle">
                  {activeChild.full_name}
                </h3>
                <ChildTimetable
                  child={activeChild}
                  lessons={lessons}
                  selectedDate={selectedDate}
                  locale={locale}
                  language={language}
                  t={t}
                />
              </section>
            </div>
          ) : (
            <div className="lessons-empty">
              <p>{t("lessons.empty")}</p>
            </div>
          )
        ) : (
          <>
            <section className="lessons-toolbar">
              <div className="lessons-filters">
                <label className="lessons-filter">
                  <span>{t("lessons.searchLabel")}</span>
                  <input
                    type="search"
                    placeholder={t("lessons.searchPlaceholder")}
                    value={searchTerm}
                    onChange={(event) => setSearchTerm(event.target.value)}
                  />
                </label>
                <label className="lessons-filter">
                  <span>{t("lessons.statusFilter")}</span>
                  <select
                    value={statusFilter}
                    onChange={(event) => setStatusFilter(event.target.value)}
                  >
                    {STATUS_FILTERS.map((filter) => (
                      <option key={filter.value} value={filter.value}>
                        {t(filter.labelKey)}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
              <button
                type="button"
                className="lessons-action lessons-action--ghost"
                onClick={handleRefresh}
              >
                {t("lessons.refresh")}
              </button>
            </section>

            {error && (
              <div className="lessons-error" role="alert">
                <p>
                  {t("lessons.error")}
                  {error}
                </p>
              </div>
            )}

            {loading ? (
              <p className="lessons-muted">{t("lessons.loading")}</p>
            ) : filteredLessons.length ? (
              <div className="lessons-list">
                {filteredLessons.map((lesson) => (
                  <article className="lessons-item" key={lesson.id}>
                    <div className="lessons-item__primary">
                      <h3>{localizeField(lesson, "title", language)}</h3>
                      {lesson.description && (
                        <p className="lessons-item__description">
                          {localizeField(lesson, "description", language)}
                        </p>
                      )}
                      {lesson?.class_group?.id || lesson?.class_group_id ? (
                        <p className="lessons-muted">
                          {t("lessons.timetable.classLabel")}{" "}
                          {localizeField(
                            lesson.class_group,
                            "name",
                            language,
                          ) ||
                            lesson?.class_group?.name ||
                            "—"}
                        </p>
                      ) : (
                        <p className="lessons-muted">
                          {t("lessons.student")}:{" "}
                          {localizeField(
                            lesson.student,
                            "full_name",
                            language,
                          ) || "—"}
                        </p>
                      )}
                      <p className="lessons-muted">
                        {t("lessons.teacher")}:{" "}
                        {localizeField(lesson.teacher, "full_name", language) ||
                          lesson.teacher?.email}
                      </p>
                      {lesson.pack && (
                        <p className="lessons-muted">
                          {t("lessons.pack")}:{" "}
                          {localizeField(lesson.pack, "name", language)} (
                          {localizeField(lesson.pack.subject, "name", language)}
                          )
                        </p>
                      )}
                    </div>
                    <div className="lessons-item__meta">
                      <span className={statusClassName(lesson.status)}>
                        {t(STATUS_LABELS_KEY[lesson.status]) || lesson.status}
                      </span>
                      <span className="lessons-item__date">
                        {formatDateTime(lesson.scheduled_at, language)}
                      </span>
                      <span className="lessons-muted">
                        {t("lessons.duration")}: {lesson.duration_minutes}{" "}
                        {t("lessons.minutes")}
                      </span>
                    </div>
                    <div className="lessons-item__actions">
                      {lesson.meeting_url ? (
                        <a
                          href={lesson.meeting_url}
                          className="lessons-action lessons-action--primary"
                          target="_blank"
                          rel="noreferrer"
                        >
                          {t("lessons.joinMeeting")}
                        </a>
                      ) : null}
                      <Link
                        to={`/lessons/${lesson.id}`}
                        className="lessons-action lessons-action--ghost"
                      >
                        {t("lessons.viewDetails")}
                      </Link>
                      {canManage && (
                        <Link
                          to={`/lessons/${lesson.id}/edit`}
                          className="lessons-action lessons-action--ghost"
                        >
                          {t("lessons.edit")}
                        </Link>
                      )}
                      {canManage && (
                        <button
                          type="button"
                          className="lessons-action lessons-action--danger"
                          onClick={() => handleDelete(lesson.id)}
                        >
                          {t("lessons.delete")}
                        </button>
                      )}
                    </div>
                  </article>
                ))}
              </div>
            ) : (
              <div className="lessons-empty">
                <p>{t("lessons.empty")}</p>
                {canManage ? (
                  <Link to="/lessons/new" className="btn primary">
                    {t("lessons.emptyCta")}
                  </Link>
                ) : null}
              </div>
            )}

            <Pagination
              page={lessonsPage}
              totalPages={lessonsTotalPages}
              dir={language === "ar" ? "rtl" : "ltr"}
              disabled={loading}
              onPageChange={(nextPage) => setLessonsPage(nextPage)}
            />
          </>
        )}
      </div>
    </div>
  );

  return effectiveRole === "student" ? (
    <StudentShell>{page}</StudentShell>
  ) : (
    page
  );
}
